/**
 * Modal de configuração para node de Filtrar Array
 * Permite configurar múltiplas condições de filtro com operadores E/OU
 * 
 * RELEASE: 1.0.0 - 2024-12-24 - Novo modal para configurar filtro de arrays
 * RELEASE: 2.1.0 - 2026-09-07 - Variável de saída migrada para o modal de destino.
 * RELEASE: 2.0.0 - 2024-12-30 - Suporte a múltiplas condições com E/OU
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FlowNode, FilterArrayConfig, FilterArrayCondition } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { HelpCircle, Filter, ArrowRight, Plus, Trash2 } from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariablePickerCombobox } from '../VariablePickerCombobox';
import { VariableDestinationPicker } from '../VariableDestinationPicker';
import { VariableInsertButton } from '../VariableInsertButton';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

/**
 * Operadores disponíveis para filtro
 */
const filterOperators = [
  { value: 'equals', label: 'Igual a' },
  { value: 'not_equals', label: 'Diferente de' },
  { value: 'contains', label: 'Contém' },
  { value: 'not_contains', label: 'Não contém' },
  { value: 'starts_with', label: 'Começa com' },
  { value: 'ends_with', label: 'Termina com' },
  { value: 'greater', label: 'Maior que' },
  { value: 'less', label: 'Menor que' },
  { value: 'greater_equal', label: 'Maior ou igual' },
  { value: 'less_equal', label: 'Menor ou igual' },
  { value: 'is_set', label: 'Está definido' },
  { value: 'is_empty', label: 'Está vazio' },
];

/**
 * Operadores que não precisam de valor
 */
const noValueOperators = ['is_set', 'is_empty'];

/**
 * Schema de validação para condição individual
 */
const conditionSchema = z.object({
  id: z.string(),
  field_path: z.string().min(1, 'Campo é obrigatório'),
  operator: z.string().min(1, 'Operador é obrigatório'),
  value: z.string().optional(),
});

/**
 * Schema de validação completo
 */
const filterArraySchema = z.object({
  source_variable: z.string().min(1, 'Variável de entrada é obrigatória'),
  output_variable: z.string().min(1, 'Variável de saída é obrigatória'),
  conditions: z.array(conditionSchema).min(1, 'Pelo menos uma condição é necessária'),
  logicOperator: z.enum(['and', 'or']),
});

interface ConfigureFilterArrayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: FilterArrayConfig) => void;
}

/**
 * Gera UUID simples para condição
 */
const generateConditionId = () => `cond_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * Cria condição vazia padrão
 */
const createEmptyCondition = (): FilterArrayCondition => ({
  id: generateConditionId(),
  field_path: '',
  operator: 'equals',
  value: '',
});

/**
 * Dialog de configuração de Filtrar Array com múltiplas condições
 */
export function ConfigureFilterArrayDialog({ 
  open, 
  onOpenChange, 
  node, 
  onSave 
}: ConfigureFilterArrayDialogProps) {
  const { currentTenant } = useAuth();
  const [sourceVariable, setSourceVariable] = useState('');
  const [outputVariable, setOutputVariable] = useState('');
  const [conditions, setConditions] = useState<FilterArrayCondition[]>([createEmptyCondition()]);
  const [logicOperator, setLogicOperator] = useState<'and' | 'or'>('and');
  const [showHelp, setShowHelp] = useState(false);
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);

  /**
   * Sincroniza estados quando node mudar
   * Suporta formato novo (conditions) e legado (field_path, operator, filter_value)
   */
  useEffect(() => {
    if (node) {
      const config = node.data.config || {};
      setSourceVariable(config.source_variable || '');
      setOutputVariable(config.output_variable || '');
      
      // Verifica se é formato novo ou legado
      if (config.conditions && Array.isArray(config.conditions) && config.conditions.length > 0) {
        // Formato novo: múltiplas condições
        setConditions(config.conditions);
        setLogicOperator(config.logicOperator || 'and');
      } else if (config.field_path) {
        // Formato legado: converte para novo formato
        setConditions([{
          id: generateConditionId(),
          field_path: config.field_path,
          operator: config.operator || 'equals',
          value: config.filter_value || '',
        }]);
        setLogicOperator('and');
      } else {
        // Novo node: inicia com condição vazia
        setConditions([createEmptyCondition()]);
        setLogicOperator('and');
      }
    }
  }, [node]);

  /**
   * Adiciona nova condição
   */
  const addCondition = () => {
    setConditions([...conditions, createEmptyCondition()]);
  };

  /**
   * Remove condição (mínimo 1)
   */
  const removeCondition = (conditionId: string) => {
    if (conditions.length <= 1) {
      toast.error('É necessário pelo menos uma condição');
      return;
    }
    setConditions(conditions.filter(c => c.id !== conditionId));
  };

  /**
   * Atualiza campo específico de uma condição
   */
  const updateCondition = (conditionId: string, field: keyof FilterArrayCondition, value: string) => {
    setConditions(conditions.map(c => 
      c.id === conditionId ? { ...c, [field]: value } : c
    ));
  };

  /**
   * Verifica se operador precisa de valor
   */
  const operatorNeedsValue = (operator: string) => !noValueOperators.includes(operator);

  /**
   * Salva a configuração
   */
  const handleSave = () => {
    if (!node) return;

    try {
      // Valida condições antes de salvar
      const cleanedConditions = conditions.map(c => ({
        ...c,
        value: operatorNeedsValue(c.operator) ? c.value : '',
      }));

      // Validação adicional para operadores que precisam de valor
      for (const cond of cleanedConditions) {
        if (operatorNeedsValue(cond.operator) && !cond.value.trim()) {
          toast.error(`Valor é obrigatório para o operador "${filterOperators.find(o => o.value === cond.operator)?.label}"`);
          return;
        }
      }

      const validatedData = filterArraySchema.parse({
        source_variable: sourceVariable,
        output_variable: outputVariable,
        conditions: cleanedConditions,
        logicOperator: logicOperator,
      });

      const config: FilterArrayConfig = {
        source_variable: validatedData.source_variable,
        output_variable: validatedData.output_variable,
        conditions: validatedData.conditions as FilterArrayCondition[],
        logicOperator: validatedData.logicOperator,
      };

      onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as any);
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast.error(firstError.message);
      } else {
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  /**
   * Retorna label do operador lógico
   */
  const getLogicLabel = () => logicOperator === 'and' ? 'E' : 'OU';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-amber-500" />
                Configurar Filtrar Array
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Campo ID do Node (somente leitura) */}
            <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

            {/* Variável de Entrada */}
            <div className="space-y-2">
              <Label>Variável de Entrada (Array) *</Label>
              <VariablePickerCombobox
                tenantId={currentTenant?.id || ''}
                value={sourceVariable}
                onChange={(variable) => {
                  setSourceVariable(variable?.name || '');
                }}
                allowClear={true}
                placeholder="Selecione a variável de entrada"
              />
              <p className="text-xs text-muted-foreground">
                Variável que contém o array a ser filtrado (ex: resposta do webhook)
              </p>
            </div>

            {/* Condições de Filtro */}
            <div className="space-y-3">
              <Label>Condições de Filtro *</Label>
              
              {conditions.map((condition, index) => (
                <div key={condition.id} className="space-y-2">
                  {/* Separador com operador lógico entre condições */}
                  {index > 0 && (
                    <div className="flex items-center gap-2 py-2">
                      <div className="flex-1 h-px bg-border" />
                      <Select 
                        value={logicOperator} 
                        onValueChange={(v) => setLogicOperator(v as 'and' | 'or')}
                      >
                        <SelectTrigger className="w-20 h-8 text-xs font-medium">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="and">E</SelectItem>
                          <SelectItem value="or">OU</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                  )}

                  {/* Card da condição */}
                  <div className="border border-border rounded-lg p-3 space-y-3 bg-muted/30">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">
                        Condição {index + 1}
                      </span>
                      {conditions.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive hover:text-destructive"
                          onClick={() => removeCondition(condition.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>

                    {/* Campo a verificar */}
                    <div className="space-y-1">
                      <Label className="text-xs">Campo</Label>
                      <Input
                        placeholder="status"
                        value={condition.field_path}
                        onChange={(e) => updateCondition(condition.id, 'field_path', e.target.value)}
                        maxLength={100}
                        className="h-9"
                      />
                    </div>

                    {/* Operador e Valor */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Operador</Label>
                        <Select 
                          value={condition.operator} 
                          onValueChange={(v) => updateCondition(condition.id, 'operator', v)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {filterOperators.map((op) => (
                              <SelectItem key={op.value} value={op.value}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Campo Valor com suporte a variáveis do tenant */}
                      {operatorNeedsValue(condition.operator) && (
                        <div className="space-y-1">
                          <Label className="text-xs">Valor</Label>
                          <div className="flex gap-1">
                            <Input
                              placeholder="A ou {{variavel}}"
                              value={condition.value}
                              onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
                              maxLength={200}
                              className="h-9 flex-1"
                            />
                            <VariableInsertButton
                              tenantId={currentTenant?.id || ''}
                              onSelectVariable={(variable) => {
                                // Insere a variável no formato {{nome}}
                                updateCondition(condition.id, 'value', `{{${variable.name}}}`);
                              }}
                              variant="icon"
                              className="h-9 w-9"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Botão adicionar condição */}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={addCondition}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Condição
              </Button>
            </div>

            {/* Variável de Saída */}
            <div className="space-y-2">
              <Label>Variável de Saída *</Label>
              <VariableDestinationPicker
                tenantId={currentTenant?.id || ''}
                value={outputVariable}
                valueName={outputVariable}
                onChange={(variable) => {
                  setOutputVariable(variable?.name || '');
                }}
                allowClear
                placeholder="Selecione ou crie a variável de saída"
              />
              <p className="text-xs text-muted-foreground">
                Variável onde o array filtrado será salvo
              </p>
            </div>

            {/* Preview visual */}
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <p className="text-sm font-medium mb-3">Pré-visualização:</p>
              <div className="space-y-2 text-sm font-mono">
                {/* Entrada */}
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-amber-500/20 text-amber-700 dark:text-amber-400 rounded">
                    {sourceVariable || 'array_entrada'}
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">filtrar onde</span>
                </div>
                
                {/* Condições */}
                <div className="pl-4 space-y-1">
                  {conditions.map((cond, idx) => (
                    <div key={cond.id} className="flex items-center gap-2 flex-wrap">
                      {idx > 0 && (
                        <span className="text-xs font-bold text-primary px-1">
                          {getLogicLabel()}
                        </span>
                      )}
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-700 dark:text-blue-400 rounded">
                        {cond.field_path || 'campo'}
                      </span>
                      <span className="text-muted-foreground">
                        {filterOperators.find(o => o.value === cond.operator)?.label || cond.operator}
                      </span>
                      {/* Preview com indicação visual de variável vs valor literal */}
                      {operatorNeedsValue(cond.operator) && (
                        <span className={cn(
                          "px-2 py-1 rounded",
                          // Se for variável usa cor roxa, senão verde
                          cond.value?.startsWith('{{') && cond.value?.endsWith('}}')
                            ? "bg-purple-500/20 text-purple-700 dark:text-purple-400"
                            : "bg-green-500/20 text-green-700 dark:text-green-400"
                        )}>
                          {cond.value?.startsWith('{{') && cond.value?.endsWith('}}')
                            ? cond.value  // Mostra {{variavel}} como está
                            : `"${cond.value || 'valor'}"`  // Mostra valor literal entre aspas
                          }
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Saída */}
                <div className="flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <span className="px-2 py-1 bg-cyan-500/20 text-cyan-700 dark:text-cyan-400 rounded">
                    {outputVariable || 'array_saida'}
                  </span>
                </div>
              </div>
            </div>

            {/* Dica de uso */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <p className="text-sm text-blue-700 dark:text-blue-400">
                💡 <strong>Dica:</strong> Use múltiplas condições para filtrar com mais precisão. 
                Por exemplo: status = "A" <strong>E</strong> valor &gt; 100 para filtrar boletos 
                abertos acima de R$ 100.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NodeHelpDialog 
        open={showHelp} 
        onOpenChange={setShowHelp} 
        nodeType="filter_array" 
      />
    </>
  );
}
